import { c as _c } from "react/compiler-runtime";
import { getTeams } from "@api/teams-api-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { sentryCaptureError } from "@utils/sentry-utils";
import { useEffect, useState } from "react";

/** The teams of each member, by the identity of the member */

/** What was loaded, together with the load it belongs to so that an outdated result is ignored */

/** Kept as a constant so that nothing loaded keeps returning the same reference */
const NO_TEAMS = new Map();

/** Separates the identities in the key of a load, since an identity never contains a line break */
const IDENTITY_SEPARATOR = "\n";

/**
 * Loads the teams each member is in already, so that the members who are in a team can be told
 * apart before anybody is sent to it, whatever the size of the team.
 *
 * It uses the same lookup as the Teams tab of a member profile, once for each member. The members
 * of an action are capped at a few, so this takes a few small requests at most. The result only
 * matters to the dialog that asks for it, so it is kept here instead of in the store, like the
 * export of members does.
 *
 * @param members The members whose teams are loaded
 * @param isEnabled Whether to load them, e.g. only while a dialog is open
 */
export function useMembersTeamIds(members, isEnabled) {
  const $ = _c(13);
  const companyId = useCompanyId();
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);
  let t0;
  if ($[0] !== members) {
    t0 = members.map(_temp).join(IDENTITY_SEPARATOR);
    $[0] = members;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const identitiesKey = t0;
  const loadKey = `${attempt}${IDENTITY_SEPARATOR}${identitiesKey}`;
  let t1;
  let t2;
  if ($[2] !== companyId || $[3] !== identitiesKey || $[4] !== isEnabled || $[5] !== loadKey) {
    t1 = () => {
      if (!isEnabled || !identitiesKey) {
        return;
      }
      let isOutdated = false;
      const identities = identitiesKey.split(IDENTITY_SEPARATOR);
      Promise.all(identities.map(async identity => {
        const teams = await getTeams({
          companyId,
          memberIdentity: identity
        });
        return [identity, new Set(teams.map(_temp2))];
      })).then(entries => {
        if (!isOutdated) {
          setResult({
            loadKey,
            teamIdsByMember: new Map(entries),
            hasFailed: false
          });
        }
      }).catch(error => {
        if (!isOutdated) {
          sentryCaptureError({
            error,
            title: "Failed to load the member teams"
          });
          setResult({
            loadKey,
            teamIdsByMember: NO_TEAMS,
            hasFailed: true
          });
        }
      });
      return () => {
        isOutdated = true;
        setResult(null);
      };
    };
    t2 = [companyId, identitiesKey, isEnabled, loadKey];
    $[2] = companyId;
    $[3] = identitiesKey;
    $[4] = isEnabled;
    $[5] = loadKey;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  const current = result?.loadKey === loadKey ? result : undefined;
  const t3 = current?.teamIdsByMember ?? NO_TEAMS;
  const t4 = isEnabled && !!identitiesKey && !current;
  const t5 = !!current?.hasFailed;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setAttempt(_temp3);
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t7 = {
      teamIdsByMember: t3,
      isLoading: t4,
      hasFailed: t5,
      retry: t6
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
function _temp3(previous) {
  return previous + 1;
}
function _temp2(team) {
  return team.id;
}
function _temp(member) {
  return member.identity;
}